import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Flame, GlassWater, UtensilsCrossed, ArrowLeft, Search, Target } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = [
  { key: 'drinks', label: 'Drinks', icon: GlassWater, accent: 'text-blue-accent border-blue-accent/30 bg-blue-accent/10' },
  { key: 'food', label: 'Food', icon: UtensilsCrossed, accent: 'text-amber-400 border-amber-500/30 bg-amber-500/10' },
  { key: 'shisha', label: 'Shisha', icon: Flame, accent: 'text-gold border-gold/30 bg-gold/10' },
  { key: 'activities', label: 'Activities', icon: Target, accent: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10' },
];

/**
 * Public, read-only menu display page.
 *
 * Designed to be shown to a customer at the lounge kiosk — pure browsing,
 * no cart, no ordering. The kiosk operator clicks "Menu" in the sidebar,
 * hands the tablet to the customer, customer flicks through categories.
 *
 * No auth required — same `/menu` endpoint the public ordering pages use.
 */
export default function MenuDisplayPage() {
  const navigate = useNavigate();
  const [menu, setMenu] = useState([]);
  const [active, setActive] = useState('drinks');
  const [q, setQ] = useState('');

  const handleBack = () => {
    window.close();
    // If the tab is still open after a tick, fall back to home.
    setTimeout(() => {
      if (!window.closed) {
        if (window.history.length > 1) navigate(-1);
        else navigate('/');
      }
    }, 80);
  };

  const fetchMenu = useCallback(async () => {
    try {
      const { data } = await api.get('/menu');
      setMenu(data || []);
    } catch { /* */ }
  }, []);
  useEffect(() => { fetchMenu(); }, [fetchMenu]);

  const items = menu
    .filter(i => i.category === active)
    .filter(i => !q || i.name.toLowerCase().includes(q.toLowerCase()) || (i.description || '').toLowerCase().includes(q.toLowerCase()));

  // Group items by subcategory for clean display
  // Items missing a subcategory get a hidden marker so we don't render an
  // "Other" header — they just appear under the category cleanly.
  const UNCAT_KEY = '__uncategorized__';
  const grouped = items.reduce((acc, it) => {
    const sub = it.subcategory || UNCAT_KEY;
    if (!acc[sub]) acc[sub] = [];
    acc[sub].push(it);
    return acc;
  }, {});
  const subOrder = Object.keys(grouped).sort();

  return (
    <div className="min-h-screen bg-bg-primary text-white" data-testid="menu-display-page">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-bg-primary/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between gap-4">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-zinc-500 hover:text-zinc-200 transition text-sm font-sans"
            data-testid="menu-display-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="text-center">
            <h1 className="text-2xl font-serif text-gold">Majnu</h1>
            <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-sans">Menu</p>
          </div>
          <div className="w-16" />
        </div>

        {/* Category tabs */}
        <div className="max-w-6xl mx-auto px-3 pb-3 flex items-center gap-2">
          {CATEGORIES.map(c => (
            <button
              key={c.key}
              onClick={() => setActive(c.key)}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-sm border transition font-sans text-sm ${active === c.key ? c.accent : 'border-white/10 text-zinc-500 hover:text-zinc-300 hover:border-white/20'}`}
              data-testid={`menu-display-tab-${c.key}`}
            >
              <c.icon className="w-4 h-4" />
              <span className="font-medium">{c.label}</span>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="max-w-6xl mx-auto px-5 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search this category…"
              className="w-full bg-white/[0.04] border border-white/10 rounded-sm pl-10 pr-4 py-2.5 text-sm text-white font-sans placeholder:text-zinc-600 focus:outline-none focus:border-white/30"
              data-testid="menu-display-search"
            />
          </div>
        </div>
      </header>

      {/* Items */}
      <main className="max-w-6xl mx-auto px-5 py-6 pb-20">
        {subOrder.length === 0 && (
          <p className="text-zinc-600 text-center py-16 font-sans" data-testid="menu-display-empty">No items match your search.</p>
        )}
        {subOrder.map(sub => (
          <section key={sub} className="mb-8">
            {sub !== UNCAT_KEY && (
              <h2 className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-sans font-bold mb-3 pl-1">{sub}</h2>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {grouped[sub].map(item => {
                const showThumb = item.category !== 'shisha';
                const fallbackIcon = active === 'drinks' ? <GlassWater className="w-8 h-8" /> : active === 'food' ? <UtensilsCrossed className="w-8 h-8" /> : active === 'activities' ? <Target className="w-8 h-8" /> : <Flame className="w-8 h-8" />;
                return (
                  <article
                    key={item.id}
                    className="bg-white/[0.03] border border-white/5 rounded-sm overflow-hidden card-hover"
                    data-testid={`menu-display-item-${item.id}`}
                  >
                    <div className="flex gap-3 p-3">
                      {showThumb && (
                        <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-sm overflow-hidden shrink-0 bg-bg-secondary flex items-center justify-center text-zinc-700">
                          {item.image_path ? (
                            <img src={`${API_BASE}/api/files/${item.image_path}`} alt={item.name} className="w-full h-full object-cover" loading="lazy" />
                          ) : (
                            fallbackIcon
                          )}
                        </div>
                      )}
                      <div className="flex-1 min-w-0 flex flex-col">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm text-white font-sans font-medium leading-tight">{item.name}</p>
                          {item.variants && item.variants.length > 0 ? (
                            <p className="text-xs text-gold font-mono whitespace-nowrap">
                              ${Math.min(...item.variants.map(v => v.price)).toFixed(2)}{item.variants.length > 1 ? '+' : ''}
                            </p>
                          ) : (
                            <p className="text-xs text-gold font-mono whitespace-nowrap">${(item.price || 0).toFixed(2)}</p>
                          )}
                        </div>
                        {item.description && (
                          <p className="text-[11px] text-zinc-500 font-sans line-clamp-2 mt-1">{item.description}</p>
                        )}
                        {item.is_out_of_stock && (
                          <span className="mt-auto pt-2 text-[9px] text-red-400 uppercase tracking-wider">Out of stock</span>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        ))}
      </main>
    </div>
  );
}
